/** Preview box in px. Keep in sync with `--intranet-image-preview` in `index.css`. */
export const INTRANET_IMAGE_PREVIEW_PX = 680;

export type IntranetImageFit = 'wide' | 'portrait';

/**
 * Portrait fills a square and blurs the side bars. Wide and square images keep a fixed width
 * and a height from the file. Unknown sizes stay wide until the file has loaded.
 */
export function intranetImageFit(width: number, height: number): IntranetImageFit {
  if (!(width > 0) || !(height > 0)) {
    return 'wide';
  }
  return height > width ? 'portrait' : 'wide';
}

export function assignIntranetImageFit(frame: HTMLElement, image: HTMLImageElement): void {
  if (!(image.naturalWidth > 0) || !(image.naturalHeight > 0)) {
    return;
  }
  frame.dataset.fit = intranetImageFit(image.naturalWidth, image.naturalHeight);
}

export function enlargeLabel(alt: string): string {
  const name = alt.trim();
  return name ? `Bild vergrößern: ${name}` : 'Bild vergrößern';
}

/**
 * Wraps each body image in the preview frame. The stored HTML stays `<img>`; only the reader
 * changes. Returns a cleanup that puts the original images back.
 */
export function frameIntranetBodyImages(root: ParentNode, expandable: boolean): () => void {
  const images = [...root.querySelectorAll('img')].filter(
    (img): img is HTMLImageElement =>
      img instanceof HTMLImageElement && img.closest('.intranet-image-frame') === null,
  );
  const cleanups = images.map((img) => frameBodyImage(img, expandable));
  return () => {
    for (const cleanup of cleanups) {
      cleanup();
    }
  };
}

function frameBodyImage(img: HTMLImageElement, expandable: boolean): () => void {
  const parent = img.parentNode;
  if (!parent) {
    return () => undefined;
  }
  const frame = document.createElement('span');
  frame.className = 'intranet-image-frame';
  const blur = document.createElement('img');
  blur.alt = '';
  blur.setAttribute('aria-hidden', 'true');
  blur.className = 'intranet-image-blur';
  blur.src = img.getAttribute('src') ?? '';
  img.classList.add('intranet-image-photo', 'h-auto', 'w-full');
  parent.insertBefore(frame, img);
  frame.append(blur, img);

  const apply = () => assignIntranetImageFit(frame, img);
  img.addEventListener('load', apply);
  if (img.complete) {
    apply();
  }
  if (expandable) {
    img.tabIndex = 0;
    img.setAttribute('role', 'button');
    img.setAttribute('aria-label', enlargeLabel(img.alt));
  }

  return () => {
    img.removeEventListener('load', apply);
    img.classList.remove('intranet-image-photo', 'h-auto', 'w-full');
    img.removeAttribute('role');
    img.removeAttribute('aria-label');
    img.removeAttribute('tabindex');
    if (frame.parentNode) {
      frame.parentNode.insertBefore(img, frame);
      frame.remove();
    }
  };
}
